import React, { memo, useMemo } from "react";
import { SearchHighlightFragment } from "gql/graphql";
import MessagePlainText from "../messages/message-plain-text";

interface IProps {
  highlight?: SearchHighlightFragment | null;
  truncateString?: boolean;
}

const CONTEXT_WORDS_BEFORE_HIT = 4;
const CONTEXT_WORDS_AFTER_HIT = 8;
const MAX_WORD_LENGTH = 30;
const MAX_TOTAL_LENGTH = 200;

const truncateWord = (word: string) => {
  if (word.length <= MAX_WORD_LENGTH) return word;
  const half = Math.floor((MAX_WORD_LENGTH - 1) / 2);
  return `${word.slice(0, half)}…${word.slice(-half)}`;
};

const SearchHighlight = ({ highlight, truncateString = true }: IProps) => {
  const isFirstTextHit = highlight?.texts[0]?.type === "hit";

  const text = useMemo(() => {
    if (!highlight) return null;
    if (isFirstTextHit || !truncateString) {
      const full = highlight.texts.map((t) => truncateWord(t.value)).join("");
      return full.length > MAX_TOTAL_LENGTH
        ? `${full.slice(0, MAX_TOTAL_LENGTH)}…`
        : full;
    }

    const wordSegments: Array<{ word: string; type: string }> = [];
    highlight.texts.forEach(({ type, value }) => {
      const words = value.split(/\s+/).filter(Boolean);
      for (const word of words) {
        wordSegments.push({ word: truncateWord(word), type });
      }
    });

    const firstHitIndex = wordSegments.findIndex((w) => w.type === "hit");
    if (firstHitIndex === -1) return wordSegments.map((w) => w.word).join(" ");

    const start = Math.max(0, firstHitIndex - CONTEXT_WORDS_BEFORE_HIT);
    const end = Math.min(
      wordSegments.length,
      firstHitIndex + CONTEXT_WORDS_AFTER_HIT + 1,
    );

    const sliced = wordSegments
      .slice(start, end)
      .map((w) => w.word)
      .join(" ");
    const truncated =
      sliced.length > MAX_TOTAL_LENGTH
        ? `${sliced.slice(0, MAX_TOTAL_LENGTH)}…`
        : sliced;

    return (
      (start > 0 ? "..." : "") +
      truncated +
      (end < wordSegments.length ? "..." : "")
    );
  }, [highlight, isFirstTextHit, truncateString]);

  const highlightText = useMemo(() => {
    if (!highlight) return null;
    return highlight.texts
      .filter((text) => text.type === "hit")
      .map((text) => truncateWord(text.value));
  }, [highlight]);

  if (!highlight) {
    return null;
  }

  return <MessagePlainText text={text} highlightText={highlightText} />;
};

export default memo(SearchHighlight);
